import React, { useEffect, useRef, useState } from 'react';
import { loadAdSense } from '@/lib/adsense';

// Survives React StrictMode's dev double-mount for the same DOM node so we
// never push twice into the same <ins> (the classic "All ins elements already
// have ads" TagError).
const initializedElements = new WeakSet();

/**
 * Responsive AdSense display unit. Renders NOTHING (and loads no script) until
 * both a publisher client id and a slot id are configured via env, so it is a
 * safe backfill for empty house-ad slots.
 *
 * IMPORTANT: When AdSense has no ad to serve for the current page/geo (always
 * the case in preview, and on production whenever the inventory is empty),
 * Google marks the <ins> with data-ad-status="unfilled" but leaves it in the
 * DOM. With data-ad-format="auto" that leaves a large reserved blank block.
 * We watch the ad status (plus a load timeout) and COLLAPSE the unit to zero
 * height once we know it is unfilled, so empty slots take no visible space.
 * `onResolved(status)` lets a parent collapse its own wrapper/spacing.
 */
export const AdSenseUnit = ({
  client = process.env.REACT_APP_ADSENSE_CLIENT,
  slot = process.env.REACT_APP_ADSENSE_SLOT,
  className = '',
  style,
  onResolved,
}) => {
  const adRef = useRef(null);
  const enabled = !!(client && slot);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      onResolved && onResolved('disabled');
      return undefined;
    }
    let cancelled = false;
    let observer;
    let timer;

    const resolve = (status) => {
      if (cancelled) return;
      if (status === 'unfilled') {
        setCollapsed(true);
      }
      onResolved && onResolved(status);
    };

    loadAdSense(client)
      .then(() => {
        const el = adRef.current;
        if (cancelled || !el) return;
        if (!initializedElements.has(el) && !el.getAttribute('data-adsbygoogle-status')) {
          window.adsbygoogle = window.adsbygoogle || [];
          initializedElements.add(el);
          window.adsbygoogle.push({});
        }
        // Watch for Google's fill decision (data-ad-status: "filled"|"unfilled").
        const check = () => {
          const s = el.getAttribute('data-ad-status');
          if (s === 'unfilled') { resolve('unfilled'); return true; }
          if (s === 'filled') { resolve('filled'); return true; }
          return false;
        };
        if (!check()) {
          observer = new MutationObserver(() => { if (check() && observer) observer.disconnect(); });
          observer.observe(el, { attributes: true, attributeFilter: ['data-ad-status'] });
          // Fallback: if Google never resolves (blocked/preview), collapse.
          timer = setTimeout(() => {
            if (el.getAttribute('data-ad-status') !== 'filled') resolve('unfilled');
          }, 4000);
        }
      })
      .catch((e) => {
        console.warn('AdSense not initialized:', e?.message || e);
        resolve('unfilled');
      });

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [enabled, client]);

  if (!enabled || collapsed) return null;

  return (
    <ins
      ref={adRef}
      className={`adsbygoogle ${className}`.trim()}
      style={{ display: 'block', ...(style || {}) }}
      data-ad-client={client}
      data-ad-slot={slot}
      data-ad-format='auto'
      data-full-width-responsive='true'
      data-testid='adsense-unit'
    />
  );
};

export default AdSenseUnit;
